import React from 'react';
import { Checkbox, DatePicker, Form, Input, InputNumber, Select, Switch } from 'antd';
import type { DatePickerProps } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { FmButton } from './FmButton';
import { formatRequiredMessage } from './formValidation';
import { headingLabelText, useHostedHeading } from './fmHostedHeading';
import {
  actionVariant,
  buildRules,
  fieldIntentToStatus,
  isLocked,
  normalizeMiddleSize,
  resolveDateFieldDefaultValue,
  resolveDateFieldValue,
  selectControlClassName,
  shouldVirtualizeOptions,
} from './FmForm.helpers';
import { FmOverlayPortalContext } from './FmOverlayPortalContext';
import { FmTextFieldTrailingSubmit } from './FmTextFieldTrailingSubmit';
import { assertTextFieldTrailingSubmitIsSingleLine } from './FmTextField.validation';
import type {
  FmFieldDescriptor,
  FmFormActionDescriptor,
  FmFormFieldProps,
  FmFormSectionProps,
  FmSelectValue,
} from './FmForm.types';
import { useLanguage } from '@/i18n';

function renderSelectControl(
  descriptor: Extract<FmFieldDescriptor, { kind: 'select' }>,
  disabled: boolean,
  getPopupContainer: () => HTMLElement,
): React.ReactElement {
  return (
    <Select
      id={descriptor.id}
      value={descriptor.value}
      defaultValue={descriptor.defaultValue}
      onChange={(value) => descriptor.onChange?.(value as FmSelectValue)}
      options={descriptor.options.map(({ testId, ...option }) => ({
        ...option,
        // AntD v6 spreads unknown data-* keys of an option onto the VISIBLE
        // option row (the role="option" node lives only in the hidden a11y
        // mirror), so this is the row's addressable identity.
        ...(testId ? { 'data-testid': testId } : {}),
      }))}
      placeholder={descriptor.placeholder}
      size={descriptor.size}
      allowClear={descriptor.allowClear}
      disabled={disabled}
      loading={descriptor.loading}
      showSearch={descriptor.showSearch}
      virtual={shouldVirtualizeOptions(descriptor.options)}
      aria-label={descriptor.label || undefined}
      data-testid={descriptor.testId}
      getPopupContainer={getPopupContainer}
      variant={descriptor.variant}
      className={selectControlClassName(descriptor.controlWidth)}
      classNames={{ popup: { root: 'fm-select__popup' } }}
    />
  );
}

const renderControl = (
  descriptor: FmFieldDescriptor,
  getPopupContainer: () => HTMLElement,
): React.ReactElement => {
  const disabled = isLocked(descriptor);
  if (descriptor.kind === 'text') {
    assertTextFieldTrailingSubmitIsSingleLine(descriptor.multiline, descriptor.trailingSubmit);
    const sharedProps = {
      id: descriptor.id,
      value: descriptor.value,
      defaultValue: descriptor.defaultValue,
      onChange: descriptor.onChange,
      placeholder: descriptor.placeholder,
      maxLength: descriptor.maxLength,
      allowClear: descriptor.allowClear,
      showCount: descriptor.showCount,
      size: normalizeMiddleSize(descriptor.size),
      disabled,
      'data-testid': descriptor.testId,
      className: 'fm-text-field',
    };
    if (descriptor.multiline) {
      return <Input.TextArea {...sharedProps} rows={descriptor.rows ?? 3} />;
    }
    return (
      <Input
        {...sharedProps}
        type={descriptor.type ?? 'text'}
        suffix={
          <FmTextFieldTrailingSubmit
            descriptor={descriptor.trailingSubmit}
            fieldDisabled={disabled}
          />
        }
        onPressEnter={descriptor.onPressEnter}
      />
    );
  }
  if (descriptor.kind === 'number') {
    return (
      <InputNumber
        id={descriptor.id}
        value={descriptor.value}
        defaultValue={descriptor.defaultValue}
        onChange={(nextValue) =>
          descriptor.onChange?.(typeof nextValue === 'number' ? nextValue : null)
        }
        min={descriptor.min}
        max={descriptor.max}
        step={descriptor.step ?? 1}
        precision={descriptor.precision}
        placeholder={descriptor.placeholder}
        size={normalizeMiddleSize(descriptor.size)}
        disabled={disabled}
        parser={descriptor.parser}
        formatter={descriptor.formatter}
        stringMode={descriptor.stringMode}
        controls={false}
        data-testid={descriptor.testId}
      />
    );
  }
  if (descriptor.kind === 'select') {
    return renderSelectControl(descriptor, disabled, getPopupContainer);
  }
  if (descriptor.kind === 'date') {
    const minDate = descriptor.min ? dayjs(descriptor.min) : undefined;
    const maxDate = descriptor.max ? dayjs(descriptor.max) : undefined;
    const disabledDate: DatePickerProps<Dayjs, false>['disabledDate'] = (current) => {
      if (!current) return false;
      if (minDate && current.isBefore(minDate, 'day')) return true;
      if (maxDate && current.isAfter(maxDate, 'day')) return true;
      return false;
    };
    return (
      <DatePicker
        id={descriptor.id}
        value={resolveDateFieldValue(descriptor.value)}
        defaultValue={resolveDateFieldDefaultValue(descriptor.defaultValue)}
        onChange={(date, dateString) =>
          descriptor.onChange?.(date, Array.isArray(dateString) ? dateString[0] : dateString)
        }
        format={descriptor.format ?? 'YYYY-MM-DD'}
        placeholder={descriptor.placeholder ?? 'Select date'}
        disabled={disabled}
        disabledDate={disabledDate}
        size={normalizeMiddleSize(descriptor.size)}
        className="fm-date-field"
        data-testid={descriptor.testId}
        getPopupContainer={getPopupContainer}
        classNames={{ popup: { root: 'fm-date-field__popup' } }}
      />
    );
  }
  if (descriptor.control === 'switch') {
    return (
      <Switch
        checked={descriptor.checked}
        defaultChecked={descriptor.defaultChecked}
        onChange={descriptor.onChange}
        disabled={disabled}
        checkedChildren={descriptor.checkedLabel}
        unCheckedChildren={descriptor.uncheckedLabel}
        data-testid={descriptor.testId}
        className="fm-switch"
      />
    );
  }
  return (
    <Checkbox
      checked={descriptor.checked}
      defaultChecked={descriptor.defaultChecked}
      onChange={(event) => descriptor.onChange?.(event.target.checked)}
      disabled={disabled}
      data-testid={descriptor.testId}
      className="fm-checkbox"
    >
      {descriptor.text ?? descriptor.label}
    </Checkbox>
  );
};

export const FmFormField: React.FC<FmFormFieldProps> = ({ descriptor }) => {
  const { t } = useLanguage();
  const overlayPortalRef = React.useContext(FmOverlayPortalContext);
  const getPopupContainer = React.useCallback(
    () => overlayPortalRef?.current ?? document.body,
    [overlayPortalRef],
  );
  const reasons = [descriptor.disabledReason, descriptor.lockedReason].filter(Boolean);
  const notes = [descriptor.basisNote].filter(Boolean);
  const hasHelp = Boolean(descriptor.helper || reasons.length || notes.length);
  const help = hasHelp ? (
    <>
      {descriptor.helper ? (
        <span className="fm-form-field__helper">{descriptor.helper}</span>
      ) : null}
      {reasons.map((reason) => (
        <span className="fm-form-field__reason" key={`reason-${reason}`}>
          {reason}
        </span>
      ))}
      {notes.map((note) => (
        <span className="fm-form-field__note" key={`note-${note}`}>
          {note}
        </span>
      ))}
    </>
  ) : undefined;
  const control = renderControl(descriptor, getPopupContainer);
  const localizedRequiredMessage = formatRequiredMessage(
    t.common.ui.form.required,
    descriptor.label,
  );

  return (
    <Form.Item
      name={descriptor.name || undefined}
      label={descriptor.label}
      // Labels read "Kohde", never AntD's default "Kohde :".
      colon={false}
      required={descriptor.required || descriptor.validation?.required}
      rules={buildRules(descriptor, localizedRequiredMessage)}
      help={descriptor.error ?? help}
      validateStatus={descriptor.error ? 'error' : fieldIntentToStatus(descriptor.intent)}
      className={`fm-form-field fm-form-field--${descriptor.density ?? 'comfortable'} fm-form-field--${descriptor.layoutPreset ?? 'stacked'}`}
      data-testid={descriptor.testId ? `${descriptor.testId}-field` : undefined}
      valuePropName={descriptor.kind === 'binary' ? 'checked' : undefined}
    >
      {descriptor.unit ? (
        <span className="fm-form-field__control-with-unit">
          {control}
          <span className="fm-form-field__unit">{descriptor.unit}</span>
        </span>
      ) : (
        control
      )}
    </Form.Item>
  );
};

export const FmFormSection: React.FC<FmFormSectionProps> = ({
  title,
  hosted,
  description,
  fieldDescriptors,
  density = 'comfortable',
  layoutPreset = 'stacked',
  actionDescriptors,
  testId,
}) => {
  const fields = fieldDescriptors.map((descriptor) => (
    <FmFormField key={descriptor.name} descriptor={descriptor} />
  ));
  // One section = one title: a titled host already printed this heading, so the
  // form section either drops it or renders it below the host.
  const heading = useHostedHeading(title, 3, hosted);
  const showsTitle = Boolean(title) && heading.render;
  const HeadingTag = `h${heading.level}` as 'h3' | 'h4' | 'h5' | 'h6';

  return (
    <section
      className={`fm-form-section fm-form-section--${density} fm-form-section--${layoutPreset}`}
      // Titleless still names the region.
      aria-label={showsTitle ? undefined : headingLabelText(title)}
      data-testid={testId}
    >
      {showsTitle || description || actionDescriptors?.length ? (
        <div className="fm-form-section__header">
          <div>
            {showsTitle ? (
              <HeadingTag className="fm-form-section__title">{title}</HeadingTag>
            ) : null}
            {description ? <p className="fm-form-section__description">{description}</p> : null}
          </div>
          {actionDescriptors?.length ? (
            <div className="fm-form-section__actions">
              {actionDescriptors.map((action: FmFormActionDescriptor) => (
                <FmButton
                  key={action.id}
                  variant={actionVariant(action.intent)}
                  danger={action.intent === 'danger'}
                  onClick={action.onClick}
                >
                  {action.label}
                </FmButton>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="fm-form-section__fields">{fields}</div>
    </section>
  );
};
